import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { Form } from 'src/app/shared/entities/form.entity';
import {
  PdfTemplate,
  PdfTemplatePage,
  PdfTemplateElement,
  PdfTemplateTableValue,
} from 'src/app/shared/entities/form-pdf-template.entity';
import {
  expandPdfPagesForBoundLists,
  extractBoundListItems,
  getBoundListSliceMeta,
  isBoundListLabel,
} from 'src/app/shared/helpers/pdf-list-pagination.helper';
import {
  applyBoundTableSlice,
  expandPdfPagesForBoundTables,
  extractBoundTableData,
  findFormFieldByLabel,
  getBoundTableSliceMeta,
  getTagDataItem,
  isBoundTableLabel,
} from 'src/app/shared/helpers/pdf-table-pagination.helper';
import { PDF_DEBUG_SHOW_EMPTY_TAG_PLACEHOLDERS } from 'src/app/shared/services/form-pdf-template-generator.service';

@Component({
  selector: 'app-form-pdf-template-preview-embedded',
  templateUrl: './form-pdf-template-preview-embedded.component.html',
  styleUrls: ['./form-pdf-template-preview-embedded.component.scss'],
})
export class FormPdfTemplatePreviewEmbeddedComponent
  implements OnInit, OnChanges
{
  @Input() template!: PdfTemplate;
  @Input() form!: Form;
  @Input() tagValueMap: { [tag: string]: any } = {};
  @Input() assets: Record<string, string> = {};
  @Input() IS_DRAFT: boolean = true;
  @Input() WATERMARK_TEXT: string = 'DRAFT';

  isLoading = false;
  error: string | null = null;
  renderPages: PdfTemplatePage[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.rebuildRenderPages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tagValueMap'] || changes['template'] || changes['form']) {
      this.rebuildRenderPages();
      this.cdr.detectChanges();
    }
  }

  private rebuildRenderPages(): void {
    if (!this.template?.pages?.length) {
      this.renderPages = [];
      return;
    }

    this.renderPages = expandPdfPagesForBoundLists(
      expandPdfPagesForBoundTables(
        this.template,
        this.tagValueMap || {},
        this.form
      ),
      this.template,
      this.tagValueMap || {},
      this.form
    );
  }

  hasTagValueMap(): boolean {
    return this.tagValueMap && Object.keys(this.tagValueMap).length > 0;
  }

  getPageWidth(): string {
    return this.template?.orientation === 'portrait' ? '210mm' : '297mm';
  }

  getPageHeight(): string {
    return this.template?.orientation === 'portrait' ? '297mm' : '210mm';
  }

  getElementStyle(element: PdfTemplateElement): any {
    const sliceMeta = getBoundTableSliceMeta(element);
    const listMeta = getBoundListSliceMeta(element);
    const isSlicedList =
      listMeta.listIsContinuation || Array.isArray(listMeta.listSliceItems);
    const baseStyle: any = {
      position: 'absolute',
      left: `${element.x}mm`,
      top: `${element.y}mm`,
      width: element.width ? `${element.width}mm` : 'auto',
      height:
        sliceMeta.tableIsContinuation ||
        !!sliceMeta.tableSliceRows ||
        isSlicedList ||
        !element.height
          ? 'auto'
          : `${element.height}mm`,
      zIndex: element.zIndex || 1,
    };

    if (
      sliceMeta.tableSliceRows ||
      sliceMeta.tableContinuationLabel ||
      isSlicedList
    ) {
      baseStyle['overflow'] = 'visible';
    }

    if (element.type === 'label') {
      const labelElement = element as any;
      if (labelElement.fontSize) {
        baseStyle['fontSize'] = `${labelElement.fontSize}pt`;
      }
      if (labelElement.fontFamily) {
        baseStyle['fontFamily'] = labelElement.fontFamily;
      }
      if (labelElement.color) {
        baseStyle['color'] = labelElement.color;
      }
      if (labelElement.alignment && !sliceMeta.tableSliceRows) {
        baseStyle['textAlign'] = labelElement.alignment;
      }
      if (labelElement.fontWeight) {
        if (labelElement.fontWeight === 'bold') {
          baseStyle['fontWeight'] = 'bold';
        } else if (labelElement.fontWeight === 'italic') {
          baseStyle['fontStyle'] = 'italic';
        } else if (labelElement.fontWeight === 'bold-italic') {
          baseStyle['fontWeight'] = 'bold';
          baseStyle['fontStyle'] = 'italic';
        }
      }
    }

    if (element.type === 'line') {
      const lineElement = element as any;
      if (lineElement.rotation !== undefined) {
        baseStyle['transform'] = `rotate(${lineElement.rotation}deg)`;
        baseStyle['transformOrigin'] = 'top left';
      }
    }

    return baseStyle;
  }

  getLabelText(element: PdfTemplateElement): string {
    const labelElement = element as any;

    const isDataBindingLabel =
      labelElement.isStatic === false ||
      labelElement.needsBinding === true ||
      labelElement.bindingTagUuid;

    if (isDataBindingLabel) {
      if (labelElement.bindingTagValue) {
        const tagKey = labelElement.bindingTagValue;
        const value = this.tagValueMap ? this.tagValueMap[tagKey] : undefined;
        const fieldType =
          labelElement.fieldType ||
          findFormFieldByLabel(this.form, labelElement)?.type;
        const showPlaceholder = this.shouldShowEmptyTagPlaceholder();
        const listMeta = getBoundListSliceMeta(labelElement);

        if (
          fieldType === 'multiple-choose' ||
          Array.isArray(listMeta.listSliceItems)
        ) {
          const items = Array.isArray(listMeta.listSliceItems)
            ? listMeta.listSliceItems
            : extractBoundListItems(value);
          if (items.length === 0) {
            return this.resolveEmptyTagText(tagKey, showPlaceholder, 'List');
          }
          return items.map((item) => `• ${item}`).join('\n');
        }

        if (this.isEmptyBoundValue(value)) {
          if (fieldType === 'table') {
            return this.resolveEmptyTagText(tagKey, showPlaceholder, 'Table');
          }
          if (fieldType === 'file-upload') {
            return this.resolveEmptyTagText(tagKey, showPlaceholder, 'File');
          }
          return this.resolveEmptyTagText(tagKey, showPlaceholder);
        }

        if (fieldType === 'table') {
          return showPlaceholder ? '[Table]' : '';
        } else if (fieldType === 'file-upload') {
          if (this.isImageFile(value)) {
            return showPlaceholder ? '[Image]' : '';
          } else {
            return this.getFileName(value);
          }
        }

        if (typeof value === 'string' || typeof value === 'number') {
          return String(value);
        } else if (value && typeof value === 'object' && 'value' in value) {
          const inner = value.value;
          if (this.isEmptyBoundValue(inner)) {
            return this.resolveEmptyTagText(tagKey, showPlaceholder);
          }
          return String(inner);
        } else {
          return String(value);
        }
      }

      return this.shouldShowEmptyTagPlaceholder()
        ? '[Select Data Binding]'
        : '';
    }

    return labelElement.text || '';
  }

  private shouldShowEmptyTagPlaceholder(): boolean {
    return PDF_DEBUG_SHOW_EMPTY_TAG_PLACEHOLDERS;
  }

  private resolveEmptyTagText(
    tagName: string,
    showPlaceholder: boolean,
    suffix?: string
  ): string {
    if (!showPlaceholder) {
      return '';
    }
    return suffix ? `[${tagName} - ${suffix}]` : `[${tagName}]`;
  }

  private isEmptyBoundValue(value: unknown): boolean {
    if (value === undefined || value === null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return this.isEmptyBoundValue((value as { value: unknown }).value);
    }
    return false;
  }

  isListType(element: PdfTemplateElement): boolean {
    return isBoundListLabel(element, this.form);
  }

  isSlicedList(element: PdfTemplateElement): boolean {
    const listMeta = getBoundListSliceMeta(element);
    return (
      !!listMeta.listIsContinuation || Array.isArray(listMeta.listSliceItems)
    );
  }

  isTableType(element: PdfTemplateElement): boolean {
    return isBoundTableLabel(element, this.form);
  }

  private isImageFile(data: any): boolean {
    const imageExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.bmp',
      '.svg',
    ];

    if (typeof data === 'string') {
      const ext = data.toLowerCase().substring(data.lastIndexOf('.'));
      return imageExtensions.includes(ext);
    }

    if (Array.isArray(data) && data.length > 0) {
      const firstItem = data[0];
      if (firstItem?.url) {
        const ext = firstItem.url
          .toLowerCase()
          .substring(firstItem.url.lastIndexOf('.'));
        return imageExtensions.includes(ext);
      }
    }

    if (data && typeof data === 'object' && (data.url || data.imageUrl)) {
      const url = data.url || data.imageUrl;
      const ext = url.toLowerCase().substring(url.lastIndexOf('.'));
      return imageExtensions.includes(ext);
    }

    return false;
  }

  private getFileName(data: any): string {
    if (typeof data === 'string') {
      return data.split('/').pop() || 'file';
    }

    if (Array.isArray(data) && data.length > 0) {
      const firstItem = data[0];
      if (firstItem?.url) {
        return firstItem.url.split('/').pop() || 'file';
      }
      if (firstItem?.name) {
        return firstItem.name;
      }
    }

    if (data && typeof data === 'object') {
      if (data.name) return data.name;
      if (data.url) return data.url.split('/').pop() || 'file';
      if (data.imageUrl) return data.imageUrl.split('/').pop() || 'file';
    }

    return 'file';
  }

  getImageSrc(element: PdfTemplateElement): string | null {
    const image = element as any;

    if (image.bindingTagUuid && image.bindingTagValue) {
      const tagKey = image.bindingTagValue;
      const value = this.tagValueMap[tagKey];

      if (value) {
        if (typeof value === 'string') {
          return value;
        }

        if (Array.isArray(value) && value.length > 0) {
          const firstItem = value[0];
          if (firstItem?.url) return firstItem.url;
        }

        if (value && typeof value === 'object') {
          if (value.url) return value.url;
          if (value.imageUrl) return value.imageUrl;
          if (value.value) return value.value;
        }
      }
    }

    // Prefer in-memory preview (unsaved), then resolved form assets
    if (image.imagePreview) {
      return image.imagePreview;
    }

    if (image.assetKey && this.assets[image.assetKey]) {
      return this.assets[image.assetKey];
    }

    if (image.imageUrl) {
      return image.imageUrl;
    }

    return null;
  }

  getTableData(element: PdfTemplateElement): any {
    const tableElement = element as PdfTemplateTableValue;

    if (!tableElement.bindingTagValue && !tableElement.bindingTagUuid) {
      return null;
    }

    const tagKey = tableElement.bindingTagValue || tableElement.bindingTagUuid;
    const value = this.tagValueMap[tagKey];

    if (!value) {
      return null;
    }

    if (
      value &&
      typeof value === 'object' &&
      'data' in value &&
      'definition' in value
    ) {
      return {
        definition: value.definition,
        data: { value: value.data },
      };
    }

    return null;
  }

  getTableStructure(element: PdfTemplateElement): any {
    if (!this.isTableType(element)) {
      return null;
    }

    const labelElement = element as any;
    const value = getTagDataItem(this.tagValueMap, labelElement);
    const formField = findFormFieldByLabel(this.form, labelElement);
    const extracted = extractBoundTableData(value, formField);

    if (!extracted.fields.length) {
      return null;
    }

    return applyBoundTableSlice(extracted, element);
  }

  trackByPageUuid(index: number, page: PdfTemplatePage): string {
    return page.uuid;
  }

  trackByElementUuid(index: number, element: PdfTemplateElement): string {
    return element.uuid;
  }

  retry(): void {
    this.error = null;
    this.cdr.detectChanges();
  }
}
